import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import { MainLayout, AuthLayout, EmptyLayout } from '@/layouts'
import { setSessionEndedHandler } from '@/api/client'
import { VIEWER_HOME, isViewer, routeAllowed } from '@/utils/access'
import i18n from '@/i18n'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

/**
 * Where a navigation should go instead, for this account tier — or `true` to
 * let it through. Exported so the rule is tested without mounting pages.
 */
export function accessRedirect(
  to: Pick<RouteLocationNormalized, 'matched' | 'name'>,
  userKind: string | null | undefined
): true | RouteLocationRaw {
  const allowed = to.matched.every((r) => routeAllowed(r.meta, userKind))
  if (allowed) return true
  /* A manager who follows a link to the People screens lands on Settings,
   * where the rest of what they may change lives. */
  if (to.matched.some((r) => r.meta?.main) && !isViewer(userKind)) return { name: 'settings' }
  return to.name === VIEWER_HOME.name ? true : { ...VIEWER_HOME }
}

/* ------------------------------------------------------------------
 * Routes are grouped by layout. Pages are lazy-loaded so each one
 * becomes its own chunk. The path → mockup-file map mirrors the
 * canonical mapping in src/styles/ARCHITECTURE.md.
 * ------------------------------------------------------------------ */

/* `meta.staff: true` marks a route a client viewer has nothing on (D-008,
 * D-015): the matcher's output is invisible to them by policy, reports answer
 * 403, every create form is a write, and the dashboard is built from matches.
 * The guard below sends a viewer to their cases instead — see
 * `utils/access.ts`, which the sidebar reads too. */
const mainRoutes = [
  {
    path: '',
    name: 'dashboard',
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { title: 'Dashboard', nav: 'dashboard', staff: true },
  },

  /* TM Monitoring */
  {
    path: 'monitoring',
    name: 'monitoring',
    component: () => import('@/pages/MonitoringPage.vue'),
    meta: { title: 'TM Monitoring', nav: 'monitoring', staff: true },
  },
  {
    path: 'monitoring/matches/:id',
    name: 'match-detail',
    component: () => import('@/pages/MatchDetailPage.vue'),
    meta: { title: 'Match review', nav: 'monitoring', staff: true },
    props: true,
  },

  /* My Trademarks */
  {
    path: 'trademarks',
    name: 'trademarks',
    component: () => import('@/pages/TrademarksPage.vue'),
    meta: { title: 'My Trademarks', nav: 'trademarks' },
  },
  {
    path: 'trademarks/new',
    name: 'trademark-new',
    component: () => import('@/pages/AddTrademarkPage.vue'),
    meta: { title: 'Add trademark', nav: 'trademarks', staff: true },
  },
  {
    path: 'trademarks/:id',
    name: 'trademark-detail',
    component: () => import('@/pages/TrademarkDetailPage.vue'),
    meta: { title: 'Trademark dossier', nav: 'trademarks' },
    props: true,
  },

  /* Oppositions */
  {
    path: 'oppositions',
    name: 'oppositions',
    component: () => import('@/pages/OppositionCasesPage.vue'),
    meta: { title: 'Opposition cases', nav: 'oppositions' },
  },
  {
    path: 'oppositions/new',
    name: 'opposition-new',
    component: () => import('@/pages/OppositionCreatePage.vue'),
    meta: { title: 'Create opposition', nav: 'oppositions', staff: true },
  },
  {
    path: 'oppositions/:id',
    name: 'opposition-detail',
    component: () => import('@/pages/OppositionDetailPage.vue'),
    meta: { title: 'Opposition case', nav: 'oppositions' },
    props: true,
  },

  /* Reports */
  {
    path: 'reports',
    name: 'reports',
    component: () => import('@/pages/ReportsPage.vue'),
    meta: { title: 'Reports', nav: 'reports', staff: true },
  },
  {
    path: 'reports/:id',
    name: 'report-detail',
    component: () => import('@/pages/ReportDetailPage.vue'),
    meta: { title: 'Report detail', nav: 'reports', staff: true },
    props: true,
  },

  /* Clients */
  {
    path: 'clients',
    name: 'clients',
    component: () => import('@/pages/ClientsPage.vue'),
    meta: { title: 'Clients', nav: 'clients' },
  },
  {
    path: 'clients/new',
    name: 'client-new',
    component: () => import('@/pages/AddClientPage.vue'),
    meta: { title: 'Add client', nav: 'clients', staff: true },
  },
  {
    path: 'clients/:id/edit',
    name: 'client-edit',
    component: () => import('@/pages/AddClientPage.vue'),
    meta: { title: 'Edit client', nav: 'clients', staff: true },
    props: true,
  },
  {
    path: 'clients/:id',
    name: 'client-detail',
    component: () => import('@/pages/ClientDetailPage.vue'),
    meta: { title: 'Client', nav: 'clients' },
    props: true,
  },

  /* Account / settings / help / notifications */
  {
    path: 'notifications',
    name: 'notifications',
    component: () => import('@/pages/NotificationsPage.vue'),
    meta: { title: 'Notifications' },
  },
  {
    path: 'help',
    name: 'help',
    component: () => import('@/pages/HelpPage.vue'),
    meta: { title: 'Help center' },
  },
  {
    /* Inside the guarded `/` tree on purpose: the pipeline explanation names
     * models, volumes and costs, which is firm-facing material rather than
     * something to serve to anyone who finds the URL. */
    path: 'how-it-works',
    name: 'how-it-works',
    component: () => import('@/pages/HowItWorksPage.vue'),
    meta: { title: 'How the monitoring works' },
  },
  {
    /* Guarded for the same reason as `how-it-works`: it is a candid statement
     * of what is built and what is not, written for the firm, not for the web. */
    path: 'roadmap',
    name: 'roadmap',
    component: () => import('@/pages/RoadmapPage.vue'),
    meta: { title: 'What we\u2019re building' },
  },
  {
    path: 'account',
    name: 'account',
    component: () => import('@/pages/AccountPage.vue'),
    meta: { title: 'Account' },
  },
  {
    path: 'settings',
    name: 'settings',
    component: () => import('@/pages/SettingsPage.vue'),
    meta: { title: 'Settings', nav: 'settings' },
  },

  /* People (W3, 2026-09-30): who is in the firm and what each can see. The
   * main account's alone — `meta.main`, and `AdminCtx` on the API. */
  {
    path: 'settings/people',
    name: 'people',
    component: () => import('@/pages/PeoplePage.vue'),
    meta: { title: 'People', nav: 'settings', staff: true, main: true },
  },
  {
    path: 'settings/people/new',
    name: 'person-new',
    component: () => import('@/pages/PersonPage.vue'),
    meta: { title: 'Add a person', nav: 'settings', staff: true, main: true },
  },
  {
    path: 'settings/people/:id',
    name: 'person',
    component: () => import('@/pages/PersonPage.vue'),
    meta: { title: 'Person', nav: 'settings', staff: true, main: true },
    props: true,
  },

  /* Primitives gallery (W3.M1). Dev-only: the spread below drops it from any
   * production build, so it never ships and never needs an auth guard. */
  ...(import.meta.env.DEV
    ? [
        {
          path: 'styleguide',
          name: 'styleguide',
          component: () => import('@/pages/StyleguidePage.vue'),
          meta: { title: 'Styleguide' },
        },
      ]
    : []),
]

const authRoutes = [
  /* Placeholder — the AuthLayout shell is wired and ready; pages
   * such as /sign-in, /sign-up, /forgot-password can be added here. */
  {
    path: 'sign-in',
    name: 'sign-in',
    component: () => import('@/pages/SignInPage.vue'),
    meta: { title: 'Sign in' },
  },
  /* E-mail links (W13.M3). Public on purpose — the person following a reset
   * or an invitation has no session yet, and the link itself is the proof. */
  {
    path: 'forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { title: 'Reset your password' },
  },
  {
    path: 'set-password',
    name: 'set-password',
    component: () => import('@/pages/SetPasswordPage.vue'),
    meta: { title: 'Set your password' },
  },
]

const routes = [
  /* The whole workspace is behind auth. Declaring it once on the parent
   * beats `requiresAuth` on twenty children, where the one you forget is
   * the leak. */
  {
    path: '/',
    component: MainLayout,
    children: mainRoutes,
    meta: { requiresAuth: true },
  },
  { path: '/auth', component: AuthLayout, children: authRoutes },

  /* 404 — chromeless. Use EmptyLayout so it's clear the user is
   * outside the workspace; switch to MainLayout if you'd rather
   * keep the sidebar visible. */
  {
    path: '/:pathMatch(.*)*',
    component: EmptyLayout,
    children: [
      {
        path: '',
        name: 'not-found',
        component: () => import('@/pages/NotFoundPage.vue'),
        meta: { title: 'Not found' },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

/**
 * The browser tab's title, in the reader's language: `route.<name>` from the
 * catalogue (the route name in camelCase), falling back to the English
 * `meta.title` for a route the catalogue does not name yet. Exported so the
 * rule is tested without a browser.
 */
export function pageTitle(to: Pick<RouteLocationNormalized, 'name' | 'meta'>): string {
  const base = 'Lexentiqa'
  const key = to.name
    ? `route.${String(to.name).replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase())}`
    : ''
  const title = key && i18n.global.te(key) ? i18n.global.t(key) : to.meta?.title
  return title ? `${title} — ${base}` : base
}

router.afterEach((to) => {
  document.title = pageTitle(to)
})

/* A language switch re-titles the page the reader is already on. */
watch(i18n.global.locale, () => {
  document.title = pageTitle(router.currentRoute.value)
})

/* ─────────────────────────────────────────────────────────────
 * Auth-aware navigation guard.
 *
 * Currently a no-op because the workspace is open in mock mode.
 * Once the backend lands and routes get `meta.requiresAuth = true`
 * (the default for the / tree), this guard:
 *
 *   1. checks `useAuthStore().isAuthenticated`
 *   2. redirects to /auth/sign-in with a `?redirect=` query so the
 *      user lands back on the original page after sign-in
 *
 * Pinia must be initialised before navigation, so the import is
 * lazy — anything that depends on stores at route-resolution time
 * must follow this pattern.
 * ───────────────────────────────────────────────────────────── */
router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth.store')
  const auth = useAuthStore()

  /* Restore the session before deciding anything. Without this await, a
   * deep link into the workspace is judged against an empty store and
   * bounces a signed-in user to the login screen on every hard refresh. */
  await auth.bootstrap()

  const requiresAuth = to.matched.some((r) => r.meta?.requiresAuth)

  if (requiresAuth && !auth.isAuthenticated) {
    /* Carry the destination so sign-in can return the user to it — but not
     * for the root path, which would only add ?redirect=%2F noise. */
    const query = to.fullPath === '/' ? {} : { redirect: to.fullPath }
    return { name: 'sign-in', query }
  }

  /* Already signed in? The login page has nothing to offer — honour the
   * redirect they were carrying rather than dumping them on the dashboard. */
  if (to.name === 'sign-in' && auth.isAuthenticated) {
    const back = typeof to.query.redirect === 'string' ? to.query.redirect : null
    return back && back.startsWith('/') ? back : { name: 'dashboard' }
  }

  /* A client viewer never lands on a page that can only 403 or show nothing
   * (W11.M9) — including the dashboard, which is where sign-in sends everyone. */
  return accessRedirect(to, auth.user.userKind)
})

/* ─────────────────────────────────────────────────────────────
 * Router-level error fallback.
 *
 * Fires when a navigation fails — most commonly because a lazy
 * route's chunk failed to download (network blip, or a stale
 * client trying to load a chunk that no longer exists after a
 * fresh deploy).
 *
 * Strategy:
 *   - "ChunkLoadError" / failed dynamic import → prompt full reload
 *     (the deployed bundle has changed; only a hard refresh fixes it)
 *   - everything else → log + propagate so the global error handler
 *     can decide
 * ───────────────────────────────────────────────────────────── */
router.onError((err, to /*, from */) => {
  const msg = err?.message || ''
  const isChunkLoad =
    err?.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)

  if (isChunkLoad) {
    console.warn(
      '[Lexentiqa] Route chunk failed to load — reloading to pick up the latest bundle',
      to?.fullPath
    )
    /* Hard reload to the target so the user lands on the page
     * they were trying to reach. */
    if (typeof window !== 'undefined') {
      window.location.assign(to?.fullPath || window.location.pathname)
    }
    return
  }

  console.error('[Lexentiqa] Router navigation error', err)
})

/* ─────────────────────────────────────────────────────────────
 * The HTTP layer cannot import the router (that would be a cycle), so it
 * raises a callback instead when a refresh fails and the session is over.
 * Routing that event is the router's job, and it happens here.
 *
 * `replace` rather than `push`: an expired session should not leave a
 * history entry the back button can bounce off.
 * ───────────────────────────────────────────────────────────── */
setSessionEndedHandler(() => {
  void (async () => {
    const { useAuthStore } = await import('@/stores/auth.store')
    useAuthStore().endSession()

    const current = router.currentRoute.value
    if (current.name === 'sign-in') return
    if (!current.matched.some((r) => r.meta?.requiresAuth)) return
    const query = current.fullPath === '/' ? {} : { redirect: current.fullPath }
    await router.replace({ name: 'sign-in', query })
  })()
})

export default router
